import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { NUMBER_MONTHS_IN_YEAR } from 'constants/date';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';
import { formatNumberToCurrencyString } from 'utils/number';
import { mergePendingChanges } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';

import { PropertyAndLoanTestIds } from '../../../../../../PropertyAndLoan.testIds';
import { getPropertyTaxFormData } from '../../PropertyExpenses.utils';

import { configFields, getPropertyTaxesConfig } from './PropertyTaxesForm.config';
import { DIFF } from './PropertyTaxesForm.constants';
import { containerStyles, formControlsStyles } from './PropertyTaxesForm.styles';

const defaultObject = {};

const PropertyTaxesForm = ({
  data,
  isLoading,
  cancelForm,
  pendingChanges = defaultObject,
  saveForm,
  propertyTaxTypeOptions,
}) => {
  const dispatch = useDispatch();

  const observableFieldsNames = Object.keys(data);

  const { initialFormValues, schema, fieldsConfig } = useMemo(() => {
    const initialConfig = getPropertyTaxesConfig({ propertyTaxTypeOptions });

    return getConfigInfo({
      applicationData: getPropertyTaxFormData(data),
      config: initialConfig,
      isNestedForm: true,
    });
  }, [data, propertyTaxTypeOptions]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
    staticValidateField,
    formPendingChanges,
    setValidationErrors,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  useEffect(() => {
    if (data.frequency) {
      return;
    }

    setInForm(configFields.amountDueDates.nameInModel, [], false);
  }, [data.frequency, setInForm]);

  useEffect(() => {
    setInForm(
      configFields.annualAmount.nameInModel,
      form.monthlyAmount * NUMBER_MONTHS_IN_YEAR,
      false,
    );

    if (!form.frequency) {
      validateField(configFields.annualAmount.nameInModel);
    }
  }, [setInForm, form.monthlyAmount, form.frequency, validateField]);

  const validateAmountDueGroup = useCallback(() => {
    const annualAmount = Number(form.annualAmount);
    const dueAmounts = _.range(form.frequency?.secondaryValue).map((__, index) => {
      const value = form.amountDueDates[index]?.amountDue;

      return Number(value);
    });

    const diff = annualAmount - Number(_.sum(dueAmounts).toFixed(DIFF));
    const displayedDiff = formatNumberToCurrencyString(Math.abs(diff));
    const displayedStatus = diff > 0 ? 'more' : 'less';

    if (_.isNaN(diff) || !form.frequency) {
      return true;
    }

    const isDiff = diff !== 0;

    const annualAmountError = isDiff
      ? `It's ${displayedDiff} ${displayedStatus} than sum of Amount Due`
      : '';

    setValidationErrors(prevErrors => ({
      ...prevErrors,
      [configFields.annualAmount.nameInModel]: !annualAmount
        ? 'Required field should not be empty'
        : annualAmountError,
    }));

    return !isDiff;
  }, [form, setValidationErrors]);

  const validateDueDateGroup = useCallback(() => {
    const dueDateErrors = _.range(form.frequency?.secondaryValue).reduce((acc, __, index) => {
      const name = configFields.dueDate.nameInModel.replace('[0]', `[${index}]`);
      const value = form.amountDueDates[index]?.dueDate;

      acc[name] = !value ? 'Required field should not be empty' : '';

      return acc;
    }, {});

    if (!form.frequency) {
      return true;
    }

    setValidationErrors(prevErrors => ({ ...prevErrors, ...dueDateErrors }));

    return !_.some(dueDateErrors, Boolean);
  }, [form, setValidationErrors]);

  const onFrequencyChange = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (!option) {
        setInForm(configFields.amountDueDates.nameInModel, [], false);
      }

      validateAmountDueGroup();
    },
    [setInForm, validateAmountDueGroup, validateField],
  );

  useEffect(() => {
    setTimeout(() => validateAmountDueGroup(), 1);
  }, [validateAmountDueGroup]);

  useEffect(() => {
    setTimeout(() => validateDueDateGroup(), 2);
  }, [validateDueDateGroup]);

  const { mergedPendingChanges, isPendingChangesHighlightDisabled } = useMemo(() => {
    const mergedPendingChanges = mergePendingChanges(pendingChanges, formPendingChanges);

    return {
      mergedPendingChanges,
      isPendingChangesHighlightDisabled: Object.values(mergedPendingChanges).some(
        ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
      ),
    };
  }, [formPendingChanges, pendingChanges]);

  const formGeneratorConfig = useMemo(() => {
    return getPropertyTaxesConfig({
      propertyTaxTypeOptions,
      frequency: form.frequency?.secondaryValue,
      onFrequencyChange,
    });
  }, [propertyTaxTypeOptions, form.frequency?.secondaryValue, onFrequencyChange]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    const { schema } = getConfigInfo({ applicationData: form, config: formGeneratorConfig });

    setSchema(schema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (validationErrors.annualAmount || !(await validateForm())) {
        return;
      }

      saveForm({ updatedData: form, initialData: data });
    },
    [validationErrors.annualAmount, validateForm, saveForm, form, data],
  );

  const onCancelClick = useCallback(() => {
    if (!isFormChanged) {
      cancelForm(data);

      return;
    }

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Cancel Changes',
        description: 'Are you sure you want to cancel changes?',
        onAccept: () => {
          cancelForm(data);
        },
      }),
    );
  }, [dispatch, isFormChanged, cancelForm, data]);

  return (
    <form
      noValidate
      data-ischanged={isFormChanged}
      data-testid={PropertyAndLoanTestIds.PROPERTY_TAXES_FORM}
      onSubmit={onSubmit}
    >
      <Box sx={containerStyles}>
        <FormGenerator
          isNestedForm
          config={formGeneratorConfig}
          disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
          form={form}
          formName={FormNames.PROPERTY_TAXES}
          handleChange={handleChange}
          isLoading={isLoading}
          numberOfColumns={4}
          pendingChanges={mergedPendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        <Box sx={formControlsStyles}>
          <Button
            data-testid={PropertyAndLoanTestIds.PROPERTY_TAXES_FORM_CANCEL_BUTTON}
            size="small"
            variant="line"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
          <Button
            data-testid={PropertyAndLoanTestIds.PROPERTY_TAXES_FORM_SUBMIT_BUTTON}
            disabled={!isFormChanged}
            size="small"
            type="submit"
          >
            Add
          </Button>
        </Box>
      </Box>
    </form>
  );
};

PropertyTaxesForm.propTypes = {
  data: PropTypes.object.isRequired,
  cancelForm: PropTypes.func.isRequired,
  saveForm: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  ...WithLoadingPropTypes,
};

export default memo(PropertyTaxesForm);
